import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CellWrapper, CheckboxRegularWrapper, StyledCheckbox } from '../styles';

const CheckboxCell = ({
  setSelectedRows,
  idx,
  selectedRows,
  disabled,
  isActive = false,
  isEditing = false,
  rowIndex,
  colIndex,
  registerCellRef,
}) => {
  const cellRef = useRef(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (registerCellRef && typeof rowIndex === 'number' && typeof colIndex === 'number') {
      registerCellRef(rowIndex, colIndex, cellRef.current);

      return () => registerCellRef(rowIndex, colIndex, null);
    }

    return undefined;
  }, [registerCellRef, rowIndex, colIndex]);

  useEffect(() => {
    if (!isActive) return undefined;

    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();

        if (!disabled && !isSavingRef.current) {
          isSavingRef.current = true;

          const isChecked = selectedRows.includes(idx);
          if (isChecked) {
            setSelectedRows((prev) => prev.filter((i) => i !== idx));
          } else {
            setSelectedRows((prev) => [...prev, idx]);
          }

          setTimeout(() => {
            isSavingRef.current = false;
          }, 300);
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [isActive, disabled, selectedRows, idx, setSelectedRows]);

  const handleClick = () => {
    if (!disabled && !isSavingRef.current) {
      isSavingRef.current = true;

      const isChecked = selectedRows.includes(idx);
      if (isChecked) {
        setSelectedRows((prev) => prev.filter((i) => i !== idx));
      } else {
        setSelectedRows((prev) => [...prev, idx]);
      }

      setTimeout(() => {
        isSavingRef.current = false;
      }, 300);
    }
  };

  return (
    <CellWrapper
      ref={cellRef}
      isActive={isActive}
      isEditing={isEditing}
      onClick={handleClick}
      tabIndex={isActive ? 0 : -1}
      role="gridcell"
      aria-label={`Checkbox for row ${idx + 1}, ${selectedRows.includes(idx) ? 'checked' : 'unchecked'}`}
    >
      <CheckboxRegularWrapper>
        <StyledCheckbox
          disabled={disabled}
          type="checkbox"
          checked={selectedRows.includes(idx)}
          onChange={(e) => {
            if (!isSavingRef.current) {
              isSavingRef.current = true;

              if (e.target.checked) {
                setSelectedRows((prev) => [...prev, idx]);
              } else {
                setSelectedRows((prev) => prev.filter((i) => i !== idx));
              }

              setTimeout(() => {
                isSavingRef.current = false;
              }, 300);
            }
          }}
          tabIndex={-1}
        />
      </CheckboxRegularWrapper>
    </CellWrapper>
  );
};

CheckboxCell.propTypes = {
  setSelectedRows: PropTypes.func.isRequired,
  idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  selectedRows: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  disabled: PropTypes.bool,
  isActive: PropTypes.bool,
  isEditing: PropTypes.bool,
  rowIndex: PropTypes.number,
  colIndex: PropTypes.number,
  registerCellRef: PropTypes.func,
};

CheckboxCell.defaultProps = {
  disabled: false,
  isActive: false,
  isEditing: false,
  rowIndex: undefined,
  colIndex: undefined,
  registerCellRef: undefined,
};

export default CheckboxCell;
